/* eslint-disable no-unused-vars */
"use client";

import { useState } from "react";
import { ThumbsDown, ThumbsUp } from "lucide-react";
import { VoteTypeEnum } from "@aichat/types/index";
import { cn } from "@aichat/ui/lib/utils";
import { Typography } from "./typography";

type VotingProps = {
  onChange?: (vote: VoteTypeEnum) => void;
  disabled?: boolean;
};

const Voting = ({ onChange, disabled }: VotingProps) => {
  const [selectedVote, setSelectedVote] = useState<VoteTypeEnum | null>(null);

  const handleVote = (type: VoteTypeEnum) => {
    if (disabled) return;
    setSelectedVote(type === selectedVote ? null : type);
    if (onChange) onChange(type);
  };

  const unlikelySelected = selectedVote === VoteTypeEnum.unlikely;
  const likelySelected = selectedVote === VoteTypeEnum.likely;

  return (
    <div className="flex items-center gap-8">
      <div className="flex flex-col items-center gap-1 duration-200 group">
        <ThumbsDown
          className={cn(
            "size-10 stroke-[1.5] scale-x-[-1]",
            unlikelySelected ? "text-destructive-500" : "text-neutral-400",
            disabled ? "" : "group-hover:text-destructive-500 cursor-pointer",
          )}
          onClick={() => handleVote(VoteTypeEnum.unlikely)}
        />
        <Typography
          variant="detailMedium"
          className={cn(
            unlikelySelected ? "text-destructive-500" : "text-neutral-400",
            disabled ? "" : "group-hover:text-destructive-500",
          )}
        >
          Unlikely
        </Typography>
      </div>

      <div className="flex flex-col items-center gap-1 duration-200 group">
        <ThumbsUp
          className={cn(
            "size-10 stroke-[1.5] text-neutral-400",
            likelySelected && "text-primary-500",
            disabled ? "" : "group-hover:text-primary-500 cursor-pointer",
          )}
          onClick={() => handleVote(VoteTypeEnum.likely)}
        />
        <Typography
          variant="detailMedium"
          className={cn(
            likelySelected ? "text-primary-500" : "text-neutral-400",
            disabled ? "" : "group-hover:text-primary-500",
          )}
        >
          Likely
        </Typography>
      </div>
    </div>
  );
};

export default Voting;

type ActionVotingProps = {
  className?: string;
  wrapperClassName?: string;
  text?: string;
} & VotingProps;

export const ActionVoting = ({
  className,
  wrapperClassName,
  onChange,
  text,
  disabled,
}: ActionVotingProps) => {
  return (
    <div
      className={cn(
        "p-4 bg-white rounded-lg text-center flex flex-col items-center gap-4",
        wrapperClassName,
      )}
    >
      <Typography
        variant="h5"
        className={cn(
          "underline decoration-dashed decoration-muted-foreground underline-offset-4 whitespace-pre-line",
          className,
        )}
      >
        {text}
      </Typography>

      <Voting disabled={disabled} onChange={onChange} />
    </div>
  );
};
